import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { name } from './symbols'
import type { BuilderCompData, ViewerModel } from '@wix/thunderbolt-symbols'
import { SiteFeatureConfigSymbol, SiteAssetsClientSym, ViewerModelSym } from '@wix/thunderbolt-symbols'
import type { BuilderComponentResolver, BuilderComponentSiteConfig } from './types'
import type { SiteAssetsClientAdapter } from 'thunderbolt-site-assets-client'
import { fixExperimentsValues } from '@wix/thunderbolt-becky-root'

const LOCAL_PILER_HOST = 'localhost:5555'

const addPilerExperimentsToUrl = (url: string, experiments: Record<string, string | boolean>): string => {
	const urlObj = new URL(url)
	const experimentKeys = Object.entries(fixExperimentsValues(experiments))
		.filter(([, value]) => value === true)
		.map(([key]) => key)

	if (!experimentKeys.length) {
		return urlObj.toString()
	}

	const experimentsQueryParam = urlObj.searchParams.get('experiments')
	if (experimentsQueryParam) {
		urlObj.searchParams.set('experiments', `${experimentsQueryParam},${experimentKeys.join(',')}`)
	} else {
		urlObj.searchParams.set('experiments', experimentKeys.join(','))
	}

	return urlObj.toString()
}

const addExperimentsToUrlIfNeeded = (url: string, experiments: Record<string, string | boolean>) => {
	return isPilerRouter(url) || isCssServerlessUrl(url) ? addPilerExperimentsToUrl(url, experiments) : url
}

const modifyBuilderCompDataUrls = (data: BuilderCompData, experiments: Record<string, string | boolean>) => {
	const { componentUrl, cssUrl, sdkUrl, serverUrl, viewerRuntimeCss } = data

	data = {
		...data,
		componentUrl: addExperimentsToUrlIfNeeded(componentUrl, experiments),
		...(cssUrl && { cssUrl: addExperimentsToUrlIfNeeded(cssUrl, experiments) }),
		...(sdkUrl && { sdkUrl: addExperimentsToUrlIfNeeded(sdkUrl, experiments) }),
		...(serverUrl && { serverUrl: addExperimentsToUrlIfNeeded(serverUrl, experiments) }),
		...(viewerRuntimeCss && { viewerRuntimeCss: addExperimentsToUrlIfNeeded(viewerRuntimeCss, experiments) }),
	}
	return data
}

export const builderComponentResolver = withDependencies(
	[named(SiteFeatureConfigSymbol, name), SiteAssetsClientSym, ViewerModelSym],
	(
		config: BuilderComponentSiteConfig,
		siteAssetsClient: SiteAssetsClientAdapter,
		viewerModel: ViewerModel
	): BuilderComponentResolver => {
		function resolvePilerUrls(data: BuilderCompData) {
			const { componentUrl } = data
			if (isPilerRouter(componentUrl) || shouldResolvePilerUrl(componentUrl, viewerModel.useLocalPiler)) {
				data = modifyBuilderCompDataUrls(data, viewerModel.pilerExperiments)
			}
			if (!shouldResolvePilerUrl(componentUrl, viewerModel.useLocalPiler)) {
				return data
			}

			const appParams = extractUrlParams(componentUrl)
			const pilerSiteAssetsParams = viewerModel.siteAssets.modulesParams.pilerSiteAssets
			const baseUrl = siteAssetsClient.calcPublicModuleUrl({
				moduleParams: pilerSiteAssetsParams,
				pageCompId: appParams.pageId,
			})
			const baseUrlWithAppParams = withUrlParams(baseUrl, appParams, viewerModel.useLocalPiler)
			const serverUrl = withTargetAndContentType(baseUrlWithAppParams, 'wix', 'application/javascript')
			const sdkUrl = withTargetAndContentType(baseUrlWithAppParams, 'sdk', 'application/javascript')
			const cssUrl = withTargetAndContentType(baseUrlWithAppParams, 'css', 'text/css')

			return {
				...data,
				componentUrl: serverUrl,
				serverUrl,
				sdkUrl,
				cssUrl,
			}
		}

		function findComponentByTypeOrModuleSpecifier(typeOrSpecifier: string): BuilderCompData | undefined {
			const directMatch = config.builderComponentsData.components[typeOrSpecifier]
			if (directMatch) {
				return directMatch
			}
			// Fallback: search by moduleSpecifier
			return Object.values(config.builderComponentsData.components).find(
				(comp) => comp.moduleSpecifier === typeOrSpecifier
			)
		}

		return {
			platformEnvData() {
				const builderComponents = Object.entries(config.builderComponentsData.components).reduce(
					(acc, [builderType, componentData]) => {
						const resolved = resolvePilerUrls(componentData)
						if (resolved.sdkUrl) {
							acc[builderType] = resolved.sdkUrl
						}
						return acc
					},
					{} as Record<string, string>
				)
				return { builderComponents }
			},
			getBuilderComponentResources({ builderType }: { builderType: string }) {
				const resources = findComponentByTypeOrModuleSpecifier(builderType)
				if (!resources) {
					throw new Error(`Builder component not found: ${builderType}`)
				}
				return resolvePilerUrls(resources)
			},
		}
	}
)

const isBlocksComp = (componentUrl: string): boolean => {
	return componentUrl.includes('blocks-component')
}

const isPilerRouter = (componentUrl: string): boolean => {
	return componentUrl.includes('piler-router')
}

const isLocalPiler = (componentUrl: string, useLocalPiler: boolean): boolean => {
	return useLocalPiler && (isBlocksComp(componentUrl) || isPilerRouter(componentUrl))
}

const shouldResolvePilerUrl = (componentUrl: string, useLocalPiler: boolean = false): boolean => {
	return isBlocksComp(componentUrl) || isLocalPiler(componentUrl, useLocalPiler)
}

const isCssServerlessUrl = (componentUrl: string): boolean => {
	return componentUrl.includes('builder-component-css')
}

const extractUrlParams = (componentUrl: string) => {
	const url = new URL(componentUrl)
	const searchParams = url.searchParams

	return {
		appDefId: searchParams.get('appDefId') || '',
		pageId: searchParams.get('pageId') || '',
		wixCodeGridId: searchParams.get('wixCodeGridId') || '',
		wixCodeInstanceId: searchParams.get('wixCodeInstanceId') || '',
		siteRevision: searchParams.get('siteRevision') || '',
		siteId: searchParams.get('siteId') || '',
		metaSiteId: searchParams.get('metaSiteId') || '',
	}
}

const withTargetAndContentType = (url: string, target: string, contentType: string): string =>
	withUrlParams(url, { target, contentType })

const withUrlParams = (baseUrl: string, paramsToReplace: Record<string, string>, useLocalPiler = false): string => {
	const url = new URL(baseUrl)

	if (useLocalPiler) {
		url.protocol = 'http:'
		url.host = LOCAL_PILER_HOST
	}

	Object.entries(paramsToReplace).forEach(([key, value]) => {
		if (value) {
			url.searchParams.set(key, value)
		}
	})
	return url.toString()
}
